import React, { useEffect, useMemo, useState } from 'react';

import ListView from 'components/Crud/views/List';
import { capacityTypeDomain } from 'models';
import {
  makeStyles,
  TableCell,
  TableRow,
  IconButton,
  MuiThemeProvider
} from '@material-ui/core';
import { useAllJobTypesData, useCapacityTypesData } from 'store/selectors';
import Typography from 'components/Typography';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDispatch } from 'react-redux';
import { fetchAllJobTypes } from 'store/slices/jobTypes';
import { SortByOrder } from 'components/Crud/utils';
import reserveCapacityTheme from 'views/ReserveCapacity/reserveCapacityTheme';
import Button from 'components/Button';
import {
  ListCapacityTable,
  Column,
  TableActions,
  useTableRowActionsState,
  useTableStyles
} from './ListCapacityTable';
import AddCapacityTypeDrawer from './AddCapacityTypeDrawer';
import EditCapacityTypeDrawer from './EditCapacityTypeDrawer';
import CapacityTypeInUseModal from './CapacityTypeInUseModal';

const useStyles = makeStyles(
  theme => ({
    title: {
      marginBottom: theme.spacing(1)
    },
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: theme.spacing(10)
    },
    addCapacityTypeButton: {
      alignSelf: 'flex-end',
      textTransform: 'capitalize'
    },
    listViewContainer: {
      paddingTop: theme.spacing(5),
      // Material UI adds an override for `fontSize="variant"` on button icons, which sets the font size to 22...
      '& [class*="MuiButton-iconSizeLarge"] > *:first-child': {
        fontSize: theme.spacing(3)
      }
    },
    highlightedRow: {
      '&&&': {
        backgroundColor: theme.palette.aegis.colour.green.lightest
      }
    }
  }),
  { name: 'ListCapacityTypesView' }
);

function ListCapacityTypesView() {
  const tableClasses = useTableStyles({ containerSize: 'max' });
  const classes = useStyles();

  const capacityTypesData = useCapacityTypesData();
  const allJobTypes = useAllJobTypesData();
  const dispatch = useDispatch();
  const { rowHovered, setRowHovered } = useTableRowActionsState();

  const [sortOrder, setSortOrder] = useState<SortByOrder>(
    SortByOrder.ASCENDING
  );
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [createdId, setCreatedId] = useState<string | undefined>();

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [idToEdit, setIdToEdit] = useState<string | undefined>();
  const [editedId, setEditedId] = useState<string | undefined>();

  const [isInUseModalOpen, setIsInUseModalOpen] = useState(false);
  const [isInUseErrorMessage, setIsInUseErrorMessage] = useState<
    string | undefined
  >();

  const sortedCapacityTypes = useMemo(() => {
    return [...(capacityTypesData || [])].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortOrder === SortByOrder.ASCENDING ? comparison : -comparison;
    });
  }, [capacityTypesData, sortOrder]);

  const columnsWidth = [140, 444, 471, 50];

  const toggleAddDrawer = () => {
    setIsAddDrawerOpen(!isAddDrawerOpen);
  };

  const toggleEditDrawer = () => {
    setIsEditDrawerOpen(!isEditDrawerOpen);
  };

  const handleSortClick = () => {
    setSortOrder((prev: SortByOrder) =>
      prev === SortByOrder.ASCENDING
        ? SortByOrder.DESCENDING
        : SortByOrder.ASCENDING
    );
  };

  const openCapacityTypeInUseModal = (errorMessage: string) => {
    setIsInUseErrorMessage(errorMessage);
    setIsInUseModalOpen(true);
  };

  const closeCapacityTypeInUseModal = () => {
    setIsInUseModalOpen(false);
  };

  useEffect(() => {
    if (editedId) {
      const timer = setTimeout(() => setEditedId(undefined), 6000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [editedId]);

  useEffect(() => {
    if (createdId) {
      const timer = setTimeout(() => setCreatedId(undefined), 6000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [createdId]);

  const headers: Column[] = [
    {
      label: 'Name',
      renderButton: (className: string) => (
        <IconButton
          className={className}
          onClick={handleSortClick}
          data-testid="header-button-sort"
        >
          {sortOrder === SortByOrder.ASCENDING ? (
            <ArrowDownwardIcon fontSize="inherit" />
          ) : (
            <ArrowUpwardIcon fontSize="inherit" />
          )}
        </IconButton>
      )
    },
    { label: 'Job type' },
    { label: 'Notes' }
  ];

  const jobTypesMap = useMemo(() => {
    const map = new Map<string, string>();
    allJobTypes?.forEach(jobType => {
      map.set(jobType.id, jobType.name);
    });
    return map;
  }, [allJobTypes]);

  useEffect(() => {
    dispatch(fetchAllJobTypes());
  }, []);

  return (
    <MuiThemeProvider theme={reserveCapacityTheme}>
      <ListView
        domain={capacityTypeDomain}
        className={classes.listViewContainer}
        disableErrorDisplay
        deleteCallback={openCapacityTypeInUseModal}
      >
        {({ onDelete }) => (
          <>
            <header className={classes.pageHeader}>
              <div>
                <Typography
                  variant="h1"
                  className={classes.title}
                  data-testid="readDomainPageTitle"
                >
                  Capacity Type
                </Typography>
                <Typography
                  variant="body"
                  component="p"
                  data-testid="readDomainPageSubtitle"
                >
                  Manage Capacity across different teams and project types
                </Typography>
              </div>
              <Button
                color="primary"
                className={`${tableClasses.tableHeaderButton} ${classes.addCapacityTypeButton}`}
                data-testid={`${capacityTypeDomain.type}TableButton`}
                size="large"
                startIcon={<AddIcon fontSize="inherit" />}
                onClick={toggleAddDrawer}
              >
                Add {capacityTypeDomain.singular}
              </Button>
            </header>
            <section className={tableClasses.tableContainer}>
              <ListCapacityTable
                columnsWidth={columnsWidth}
                label={`A table of ${capacityTypeDomain.plural}`}
                columnHeaders={headers}
                domain={capacityTypeDomain}
                rowHovered={rowHovered}
                setRowHovered={setRowHovered}
              >
                {sortedCapacityTypes.map(capacityType => (
                  <TableRow
                    key={capacityType.id}
                    data-testid={`tableRow-${capacityType.id}`}
                    className={`${tableClasses.tableRow} ${
                      createdId === capacityType.id ||
                      editedId === capacityType.id
                        ? classes.highlightedRow
                        : ''
                    }`}
                    hover
                  >
                    <TableCell className={tableClasses.tableCell}>
                      {capacityType.name}
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      <div className={tableClasses.twoLineTruncate}>
                        {capacityType.jobTypes
                          ?.map(id => jobTypesMap.get(id) || id)
                          .join(', ') || ''}
                      </div>
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      <div className={tableClasses.twoLineTruncate}>
                        {capacityType.notes}
                      </div>
                    </TableCell>
                    <TableActions
                      menuItems={[
                        {
                          label: 'Edit',
                          icon: <EditIcon />,
                          onClick: () => () => {
                            setIdToEdit(capacityType.id);
                            toggleEditDrawer();
                          }
                        },
                        // TODO: https://ovotech.atlassian.net/browse/MVS-11289
                        //  {
                        //   label: 'View',
                        //   icon: <VisibilityIcon />,
                        //   onClick: onView
                        // },
                        {
                          label: 'Delete',
                          icon: <DeleteIcon />,
                          onClick: onDelete
                        }
                      ]}
                      domainItem={capacityType}
                    />
                  </TableRow>
                ))}
              </ListCapacityTable>
              <AddCapacityTypeDrawer
                isOpen={isAddDrawerOpen}
                toggleDrawer={toggleAddDrawer}
                setCreatedId={setCreatedId}
              />
              <EditCapacityTypeDrawer
                isOpen={isEditDrawerOpen}
                toggleDrawer={toggleEditDrawer}
                capacityTypeId={idToEdit as string}
                setEditedId={setEditedId}
              />
            </section>
          </>
        )}
      </ListView>
      <CapacityTypeInUseModal
        open={isInUseModalOpen}
        onClose={closeCapacityTypeInUseModal}
      >
        {isInUseErrorMessage}
      </CapacityTypeInUseModal>
    </MuiThemeProvider>
  );
}

export default ListCapacityTypesView;
